import { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import CustomModal from '../CustomModal';
import Button from '../../Button';
import { styles } from '../../../styles/themes/style';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import { useAppActions } from '../../../stores/application/useAppActions';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import { ISuppressionList, SuppressionListScope } from '../../../interfaces/models/campaign';
import { scopeOptionsFor } from '../../../utils/suppressionListUtils';

/**
 * Create a suppression list, or rename / rescope one (`list`).
 *
 * A wider scope takes the list's matching leads out of the campaigns it starts to bind,
 * so a scope change is counted first and, when it would take anyone out, the modal says
 * how many and asks again before saving.
 */

interface ISuppressionListFormModalProps {
  list?: ISuppressionList;
  onSaved?: (listId: string) => void;
}

function plural(n: number, word: string): string {
  return `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
}

const SuppressionListFormModal: FunctionComponent<ISuppressionListFormModalProps> = ({ list, onSaved }) => {
  const { hideModal } = useAppHandlers();
  const { setToast } = useAppActions();
  const { store: { user } } = useAuthStore();
  const { createSuppressionList, updateSuppressionList } = useCampaignsRequest();

  const [name, setName] = useState(list?.name ?? '');
  const [scope, setScope] = useState<SuppressionListScope>(list?.scope ?? 'all');
  const [busy, setBusy] = useState(false);
  /** Set when the new scope would take leads out: the second click saves. */
  const [reach, setReach] = useState<{ leads: number; campaigns: number } | null>(null);

  const editing = !!list;
  const trimmed = name.trim();

  const save = async () => {
    if (!trimmed || busy) return;
    setBusy(true);

    if (!editing) {
      const res = await createSuppressionList({ name: trimmed, scope });
      setBusy(false);
      if (!res?.success) {
        setToast({ type: 'error', message: res?.message || 'Could not create the list.' });
        return;
      }
      setToast({ type: 'success', message: `List “${trimmed}” created.` });
      hideModal();
      onSaved?.(res.data.list._id);
      return;
    }

    // Count first when the scope changes and nobody has seen the count yet.
    if (scope !== list!.scope && !reach) {
      const dry = await updateSuppressionList(list!._id, { scope, dryRun: true });
      if (!dry?.success) {
        setBusy(false);
        setToast({ type: 'error', message: dry?.message || 'Could not check the new scope.' });
        return;
      }
      if (dry.data.leadsAffected > 0) {
        setBusy(false);
        setReach({ leads: dry.data.leadsAffected, campaigns: dry.data.campaignsAffected });
        return;
      }
    }

    const res = await updateSuppressionList(list!._id, {
      ...(trimmed !== list!.name && { name: trimmed }),
      ...(scope !== list!.scope && { scope }),
    });
    setBusy(false);
    if (!res?.success) {
      setToast({ type: 'error', message: res?.message || 'Could not save the list.' });
      return;
    }
    const removed = res.data.leadsRemoved ?? 0;
    setToast({
      type: 'success',
      message: removed
        ? `List saved · ${plural(removed, 'lead')} taken out of ${plural(res.data.campaignsAffected, 'campaign')}.`
        : 'List saved.',
    });
    hideModal();
    onSaved?.(list!._id);
  };

  const changeScope = (next: SuppressionListScope) => {
    setScope(next);
    setReach(null);
  };

  const unchanged = editing && trimmed === list!.name && scope === list!.scope;
  // An admin editing a member's list: a reserved list stays reserved for that member.
  const foreignOwner = editing && list!.owner && String(list!.owner._id) !== String(user?._id)
    ? list!.owner.name || 'its owner'
    : undefined;

  return (
    <CustomModal
      headingText={editing ? 'Edit list' : 'New suppression list'}
      descriptionText="Name the list and choose where it applies."
      closeButton
      preventClose={busy}
      bodyPadding="0 24px 0 24px"
    >
      <Body>
        <Field>
          <Label htmlFor="suppression-list-name">Name</Label>
          <NameInput
            id="suppression-list-name"
            autoFocus
            maxLength={80}
            placeholder="e.g. Customers, Competitors, My accounts"
            value={name}
            disabled={busy}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void save(); }}
          />
        </Field>

        <Field>
          <Label as="span">Applies to</Label>
          <Options role="radiogroup">
            {scopeOptionsFor(foreignOwner).map(option => (
              <Option key={option.scope} $active={scope === option.scope}>
                <input
                  type="radio"
                  name="suppression-list-scope"
                  checked={scope === option.scope}
                  disabled={busy}
                  onChange={() => changeScope(option.scope)}
                />
                <OptionText>
                  <strong>{option.label}</strong>
                  <span>{option.description}</span>
                </OptionText>
              </Option>
            ))}
          </Options>
        </Field>

        {reach && (
          <WarnCard>
            <strong>{plural(reach.leads, 'lead')}</strong> in <strong>{plural(reach.campaigns, 'campaign')}</strong>{' '}
            match this list and will be taken out right away. Narrowing it again later does not bring them back.
          </WarnCard>
        )}
      </Body>

      <Footer>
        <Button $type="grey" $width="96px" tooltipMessage="" disabled={busy} onClickHandler={hideModal}>
          Cancel
        </Button>
        <ActionSlot>
          <Button
            $type="blue"
            $width="auto"
            tooltipMessage=""
            $customDisabled={styles.colors.primary200}
            disabled={!trimmed || unchanged}
            isLoading={busy}
            loadingText="Saving…"
            onClickHandler={() => { void save(); }}
          >
            {reach ? 'Save and remove leads' : editing ? 'Save' : 'Create list'}
          </Button>
        </ActionSlot>
      </Footer>
    </CustomModal>
  );
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  margin: 0;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  color: ${styles.colors.black600};
`;

const NameInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid ${styles.colors.black200};
  border-radius: 6px;
  font-family: NunitoSans;
  font-size: 14px;
  color: ${styles.colors.black600};
  box-sizing: border-box;

  &::placeholder { color: ${styles.colors.black300}; }
  &:focus { outline: none; border-color: ${styles.colors.primary600}; }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Option = styled.label<{ $active: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black200)};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? styles.colors.primary100 : styles.colors.white)};
  cursor: pointer;
  transition: border-color 0.14s, background 0.14s;

  input { margin: 3px 0 0; accent-color: ${styles.colors.primary600}; cursor: pointer; }
  &:hover { border-color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.primary200)}; }
`;

const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-family: NunitoSans;

  strong { font-size: 14px; color: ${styles.colors.black600}; }
  span { font-size: 12px; line-height: 17px; color: ${styles.colors.black400}; }
`;

const WarnCard = styled.div`
  padding: 12px 14px;
  border: 1px solid #f3dcae;
  border-radius: 8px;
  background: #fff8ea;
  color: #7a5310;
  font-size: 13px;
  line-height: 19px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

// The shared button pads its label by 4px; a label this long needs room to breathe.
// Styled from outside: styled(Button) would drop its $-props ($type, $width…).
const ActionSlot = styled.div`
  & > button { min-width: 120px; padding: 6px 16px; }
`;

export default SuppressionListFormModal;
